import styled, { css } from 'styled-components';
import { Button, colours, spacing, Subheader, Title, constants, CentralModal } from '@cambridgeassessment/cambridge-ui';
import { Accordion, AccordionSummary, TextField, Divider, makeStyles } from '@material-ui/core';
import { Link } from 'react-router-dom';

interface ReadOnly {
  readOnly?: boolean;
}

interface Disabled {
  disabled?: boolean;
}

interface InputProps {
  marginBottom?: number;
  bold?: boolean;
}

interface CardProps {
  isShowBorder: boolean;
}

const userInfoStyles = css`
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 1px 14px 0 rgba(206, 212, 217, 0.12), 0 5px 8px 0 rgba(206, 212, 217, 0.14),
    0 3px 5px -1px rgba(206, 212, 217, 0.2);
  border: solid 2px ${colours.porcelain};
`;

export const StyledSectionContainer = styled.div`
  font-size: 18px;
  font-weight: bold;
  padding: 8px 0;
`;

export const StyledExpansionPanel = styled(Accordion)`
  &.MuiPaper-root {
    cursor: default;
    background-color: inherit;
    width: 100%;
    box-shadow: unset;
    border: 0;
  }

  &.MuiAccordion-root {
    margin-bottom: 0;
  }

  &.MuiAccordion-root.Mui-expanded {
    margin-bottom: 0;
  }

  &.MuiAccordion-root:before {
    height: 0;
  }

  & .MuiButtonBase-root.MuiAccordionSummary-root {
    cursor: default;
    padding: 0;
  }
`;

export const StyledExpansionSummary = styled(AccordionSummary)<ReadOnly>`
  padding: 0;

  &.MuiAccordionSummary-root.Mui-expanded {
    min-height: 0px;
  }

  .MuiAccordionSummary-content {
    display: flex;
    flex-direction: column;

    &.Mui-expanded {
      margin: 12px 0;
    }
  }
  & .MuiAccordionSummary-expandIcon {
    &.Mui-expanded {
      transition: none;
      transform: none;
    }
  }

  & .MuiButtonBase-root {
    cursor: ${(props): string => (props.readOnly ? 'default' : 'pointer')};
  }

  width: 100%;
`;

export const StyledComponentHeader = styled(Subheader)`
  font-size: 26px;
  max-width: 600px;
  margin: 0;
  font-weight: bold;
`;

export const StyledComponentButton = styled(Button)`
  white-space: nowrap;
`;

export const StyledExpansionPanelDetails = styled.div`
  padding-bottom: 24px;
  flex-direction: column;
  max-width: 953px;
`;

export const StyledPoeChip = styled.div`
  padding-top: 8px;

  > :first-child {
    background-color: #f8e5ff;
  }
`;

export const StyledComponentHeaderText = styled.h3`
  font-family: SourceSansPro;
  font-size: 26px;
  font-weight: bold;
  line-height: 1.28;
  flex-shrink: 0;
  letter-spacing: normal;
  text-align: left;
  width: auto;
  margin: 0;
`;

export const StyledComponentSubHeaderText = styled.h3`
  font-family: ${constants.blissPro};
  font-size: ${constants.XLARGE};
  font-weight: bold;
  line-height: 1.28;
  flex-shrink: 0;
  letter-spacing: normal;
  text-align: left;
  width: auto;
  margin: 0;
`;

export const StyledComponentTitle = styled.h2`
  font-family: ${constants.blissPro};
  font-size: ${constants.XXLARGE};
  margin: 0;
`;

export const StyledComponentParagraphText = styled.p`
  font-size: 16px;
`;

export const StyledDivider = styled(Divider)`
  &.MuiDivider-root {
    width: 100%;
    flex-shrink: 1;
  }
`;

export const StyledComponentHeaderContainer = styled.div`
  display: flex;
  margin-top: 32px;
  margin-bottom: 20px;
  align-items: center;
`;

export const StyledParagraph = styled.p`
  font-size: 16px;
`;

export const StyledSeriesLabel = styled.div<InputProps>`
  margin-bottom: ${(props): string => (props.marginBottom ? `${props.marginBottom}px` : '0px')};
  font-weight: bold;
  font-family: SourceSansPro;
  font-size: 18px;
  line-height: 1.28;
  padding: 8px 0;
  color: #3d444d;
`;

export const StyledFieldDescription = styled.div`
  font-weight: normal;
  margin-top: 10px;
`;

export const StyledInputFieldContainer = styled.div<{ addMargin?: boolean }>`
  width: 464px;
  margin-right: ${(props) => (props.addMargin ? constants.XLARGE : '0px')};

  .MuiFormControl-marginNormal {
    margin-bottom: 4px;
  }
`;

export const StyledComponentContainer = styled.div``;

export const StyledRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 10px;
`;

export const StyledColumn = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
`;

export const StyledTextField = styled(TextField)`
  width: 296px;
  .MuiInputBase-input {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

export const StyledComponentDeadlines = styled.div`
  padding-top: 30px;
  padding-bottom: 48px;
`;

export const StyledRemoveContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  margin-left: auto;
`;

export const StyledRemoveContainerLeft = styled.div`
  display: flex;
  margin-top: 24px;
  margin-left: auto;
  justify-content: flex-end;
`;

export const StyledToggleContainer = styled.div`
  padding-right: 26px;
`;

export const StyledContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
`;

export const TitleAndSwitchFixedWidth = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  max-width: 697px;
  width: 100%;
  align-items: center;
  padding-right: ${spacing.small};
`;

export const StyledAddButtonContainer = styled.div`
  width: 230px;
  margin-top: 24px;
`;

export const StyledTextFieldContainer = styled.div`
  max-width: 464px;
`;

const StyledReadOnlyComponentCSS = css`
  pointer-events: ${(props: ReadOnly): string => (props.readOnly ? 'none' : '')};
  opacity: ${(props: ReadOnly): string => (props.readOnly ? '0.7' : '')};
`;

export const StyledReadOnlyComponent = styled.div<ReadOnly>`
  ${StyledReadOnlyComponentCSS}
  max-width: 43.5rem;
  width: 100%;
`;

export const StyledReadOnlyRenderComponent = styled.div<ReadOnly>`
  ${StyledReadOnlyComponentCSS}
`;

export const StatefulLink = styled(Link)<Disabled>`
  pointer-events: ${(props: Disabled): string => (props.disabled ? 'none' : '')};
  ${(props) => props.disabled && 'color: ' + colours.grey};
`;

export const StyledModalChildrenContainer = styled.div`
  display: flex;
  justify-content: space-between;

  & button.closeModal {
    margin-top: 0;
    padding: 0;
    width: fit-content;
    height: fit-content;
    min-width: fit-content;
  }
`;

export const ButtonText = styled.div`
  text-decoration: underline;
  color: ${colours.scienceBlue};
`;

export const StyledLink = styled.div`
  > a {
    color: ${colours.scienceBlue};
  }
`;

export const StyledActionsContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

export const StyledComponentHeaderAndLinkContainer = styled.div<CardProps>`
  margin-bottom: 8px;
  ${({ isShowBorder }) => isShowBorder && userInfoStyles};
`;

export const StyledActionsAndFileTypesContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 697px;
  width: 100%;
`;

export const StyledAccordion = styled.div`
  .MuiAccordionSummary-root {
    padding: 0px;
  }

  .MuiAccordionDetails-root {
    padding: 0px;
  }
`;

export const StyledSamplingAlgorithm = styled.div`
  padding-top: 24px;
`;

export const StyledSamplingAlgorithmSelect = styled.div`
  padding-top: 16px;
`;

export const StyledModerationLabelContainer = styled.div`
  display: inline-block;
  margin-bottom: 12px;
`;

export const MarkLabel = styled.div`
  display: inline-block;
  font-weight: 600;
  font-size: 14px;

  span {
    font-weight: normal;
    padding-left: 4px;
  }
`;

export const StyledEntriesDisclaimer = styled.div`
  width: 616px;
  font-family: ${constants.fonts.sourceSansPro};
  font-size: ${constants.MEDIUM};
  margin-top: ${spacing.tiny};
`;

export const useStyles = makeStyles({
  root: {
    fontSize: '16px',
    paddingLeft: '0px',
    marginTop: '8px',
  },
});

export const StyledBufferTitle = styled(Title)`
  font-size: 22px;
  margin: 16px 0px 8px 0px;
`;

export const StyledReportLink = styled.div`
  margin-bottom: 16px;
`;

export const StyledHorizontalLine = styled.hr`
  width: 100%;
`;

export const StyledConfirmButton = styled(Button)`
  margin-top: 32px;
`;

export const StyledCentralModal = styled(CentralModal)`
  & .MuiDialog-paper {
    width: 473px;
    height: 255px;
  }
  & .MuiTypography-root {
    color: unset;
  }
`;

export const StyledDisclaimer = styled.div`
  width: 432px;
  padding-left: ${constants.MEDIUM};
  font-family: ${constants.fonts.sourceSansPro};
  font-size: ${constants.SMALL};
  color: ${colours.nevada};
`;

export const StyledInfoText = styled.div`
  font-size: 20px;
  margin-top: 32px;
`;

export const StyledDatePickerWrapper = styled.div`
  width: 42%;
  margin-bottom: 20px;
`;
